import {Injectable} from "@angular/core";
import {Observable, Subscription} from "rxjs";
import {handleErrors} from "../misc/handle-errors.decorator";
import {SessionTimeoutDialogComponent} from "./session-timeout-dialog.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {centerPopup, defaultPopupOptions, stringifyPopupOptions} from "../misc/dom-helper";
import {AppSettingsService} from "../app-settings/app-settings.service";
import {Apollo} from "apollo-angular";
import {RenewSessionGqlModel, SessionTimeoutGqlModel} from "./session-timeout.gql-model";
import * as moment from "moment";
import {exposeMethodInDebugMode} from "../misc/expose-item-in-debug-mode.function";
import {GraphqlManagerService} from "../graphql/graphql-manager.service";
import {SuccessResultGqlModel} from "../graphql/common.gql-model";
import {StateSubject, StateSubjectState} from "../helpers/state-subject";

const loginDialogStates: StateSubjectState[] = [
  {key: "none"},
  {key: "show", cooldown: 5000},
];

@Injectable({providedIn: "root"})
export class LoginService {
  // private socket: any;
  private sessionTimeoutSubscription: Subscription;

  private dialogSubject: StateSubject =
    new StateSubject("none", loginDialogStates);

  dialog: Observable<string> = this.dialogSubject.asObservable();
  sessionTimeRemaining: number = 0;
  sessionTimeoutTime: Date = moment.utc(0).toDate();

  constructor(
    private apollo: Apollo,
    private dialogService: DialogService,
    private appSettingsService: AppSettingsService,
    private graphqlManagerService: GraphqlManagerService,
  ) {
    this.dialog.subscribe((value) => {
      switch (value) {
        case "show":
          this.dialogService.showDialog(SessionTimeoutDialogComponent, {})
            .catch((error) => { console.error(error); });
          break;
        default:
          break;
      }

      exposeMethodInDebugMode("loginInit", () => this.init());
    });

    this.appSettingsService.user.subscribe((authDetail) => {
      if (authDetail && authDetail.id && this.dialogSubject.value !== "none") {
        this.hideDialogs();
      }
    });

    window["LOGIN_REDIRECT"] = () => {
      const relayState = encodeURIComponent(`${window.location.hash}`.replace(/^#\//, ""));
      window.location.href = `/auth-hp/login?RelayState=${relayState}`;
    };

    setInterval(
      () => {
        if (this.sessionTimeoutTime.valueOf() === 0) return;

        let timeRemaining = ((this.sessionTimeoutTime as any) - (moment.utc().toDate() as any)) / 1000;
        timeRemaining = Math.floor(timeRemaining);
        if (timeRemaining < 0) timeRemaining = 0;
        this.sessionTimeRemaining = timeRemaining;

        if (this.sessionTimeRemaining < 30) {
          this.dialogSubject.next("show");
        } else {
          this.dialogSubject.next("none");
        }
      },
      1000,
    );
  }

  init() {
    if (this.sessionTimeoutSubscription && !this.sessionTimeoutSubscription.closed) {
      this.sessionTimeoutSubscription.unsubscribe();
    }

    this.graphqlManagerService.restartWebsockets();

    const query = SessionTimeoutGqlModel.query;

    this.sessionTimeoutSubscription = this.apollo.subscribe({query, fetchPolicy: "no-cache"})
      .pipe(SessionTimeoutGqlModel.mapper)
      .subscribe(
        (result: SessionTimeoutGqlModel) => {
          this.sessionTimeoutTime = result.expiry;
        },
        (error) => {
          console.error(error);
        },
      );
  }

  hideDialogs() {
    this.dialogSubject.next("none");

    if (this.sessionTimeRemaining <= 0) {
      this.init();
    }

    this.appSettingsService.load()
      .then(() => {
        this.appSettingsService.refreshBrowser(true);
      })
      .catch((error) => { console.error(error); });
  }

  @handleErrors()
  renew(): Promise<boolean> {
    const model = new RenewSessionGqlModel({});
    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(RenewSessionGqlModel.mapper)
      .toPromise()
      .then((result: SuccessResultGqlModel) => result.success);
  }

  @handleErrors()
  async logIn(): Promise<boolean> {
    const url = "/auth-hp/login?RelayState=CLOSE";
    const initialPopupBounds = centerPopup(800, 600);
    const popupOptions = stringifyPopupOptions({...defaultPopupOptions, ...initialPopupBounds});

    const win = window.open(url, "", popupOptions);
    if (!win) return false;

    const result = await new Promise<boolean>((resolve) => {
      window["LOGIN_DIALOG_DONE"] = (value) => {
        resolve(value || false);
      };

      setTimeout(() => resolve(false), 30 * 60 * 1000);
    });

    this.init();

    this.dialogSubject.next("none");

    return result;
  }
}
